/** packages/shared-components/src/hooks/useKeySelection.ts */

import { useState, useCallback, useMemo, useEffect } from "react";
import type { KeyConfig } from "../components/Keyboard/types";
import type { KeyPosition } from "../components/Keyboard";

export interface UseKeySelectionOptions {
  layout: KeyConfig[][] | null;
  mode?: "single" | "multiple";
  initialSelection?: string[];
  onSelectionChange?: (
    keys: string[],
    positions: Record<string, KeyPosition>
  ) => void;
}

export interface UseKeySelectionReturn {
  selectedKeys: string[];
  selectedPositions: Record<string, KeyPosition>;
  selectKey: (key: string) => void;
  deselectKey: (key: string) => void;
  toggleKey: (key: string) => void;
  clearSelection: () => void;
  setSelection: (keys: string[]) => void;
  selectAll: () => void;
  selectInvert: () => void;
  selectWASD: () => void;
  selectArrows: () => void;
  selectFunctionKeys: () => void;
  selectNumberRow: () => void;
  selectCustom: (codes: string[]) => void;
  isSelected: (key: string) => boolean;
  selectionCount: number;
  totalKeys: number;
  allKeys: string[];
}

/**
 * 预定义按键组
 */
const KEY_GROUPS = {
  WASD: ["KeyW", "KeyA", "KeyS", "KeyD"],
  ARROWS: ["ArrowUp", "ArrowLeft", "ArrowDown", "ArrowRight"],
  FUNCTION_KEYS: [
    "F1",
    "F2",
    "F3",
    "F4",
    "F5",
    "F6",
    "F7",
    "F8",
    "F9",
    "F10",
    "F11",
    "F12",
  ],
  NUMBER_ROW: [
    "Digit1",
    "Digit2",
    "Digit3",
    "Digit4",
    "Digit5",
    "Digit6",
    "Digit7",
    "Digit8",
    "Digit9",
    "Digit0",
  ],
};

export const useKeySelection = ({
  layout,
  mode = "multiple",
  initialSelection = [],
  onSelectionChange,
}: UseKeySelectionOptions): UseKeySelectionReturn => {
  const [selectedKeys, setSelectedKeys] = useState<string[]>(initialSelection);

  // 构建按键位置映射表 + 所有可选按键列表
  const { keyPositionMap, allKeys } = useMemo(() => {
    if (!layout) {
      return { keyPositionMap: {}, allKeys: [] };
    }

    const positionMap: Record<string, KeyPosition> = {};
    const keys: string[] = [];

    layout.forEach((row, rowIndex) => {
      row.forEach((key) => {
        // 跳过间距占位符
        if (key.code === "__gap__") return;

        if (key.originalCol !== undefined) {
          positionMap[key.code] = {
            row: rowIndex,
            col: key.originalCol,
            code: key.code,
            label: key.label || "",
            uuid: key.uuid || "",
          };
          keys.push(key.code);
        }
      });
    });

    return { keyPositionMap: positionMap, allKeys: keys };
  }, [layout]);

  // 选中的位置信息
  const selectedPositions = useMemo(() => {
    const positions: Record<string, KeyPosition> = {};
    selectedKeys.forEach((key) => {
      if (keyPositionMap[key]) {
        positions[key] = keyPositionMap[key];
      }
    });
    return positions;
  }, [selectedKeys, keyPositionMap]);

  // 触发回调
  useEffect(() => {
    onSelectionChange?.(selectedKeys, selectedPositions);
  }, [selectedKeys, selectedPositions, onSelectionChange]);

  // ========== 基础选择 ==========

  const selectKey = useCallback(
    (key: string) => {
      if (!allKeys.includes(key)) return;

      setSelectedKeys((prev) => {
        if (mode === "single") {
          return [key];
        }
        if (prev.includes(key)) {
          return prev;
        }
        return [...prev, key];
      });
    },
    [allKeys, mode]
  );

  const deselectKey = useCallback((key: string) => {
    setSelectedKeys((prev) => prev.filter((k) => k !== key));
  }, []);

  const toggleKey = useCallback(
    (key: string) => {
      if (!allKeys.includes(key)) return;

      setSelectedKeys((prev) => {
        if (mode === "single") {
          return prev.includes(key) ? [] : [key];
        }
        return prev.includes(key)
          ? prev.filter((k) => k !== key)
          : [...prev, key];
      });
    },
    [allKeys, mode]
  );

  const clearSelection = useCallback(() => {
    setSelectedKeys([]);
  }, []);

  const setSelection = useCallback(
    (keys: string[]) => {
      const validKeys = keys.filter((k) => allKeys.includes(k));
      setSelectedKeys(mode === "single" ? validKeys.slice(0, 1) : validKeys);
    },
    [allKeys, mode]
  );

  // ========== 批量选择 ==========

  const selectAll = useCallback(() => {
    if (mode === "single") return;
    setSelectedKeys([...allKeys]);
  }, [allKeys, mode]);

  const selectInvert = useCallback(() => {
    if (mode === "single") return;
    setSelectedKeys((prev) => allKeys.filter((k) => !prev.includes(k)));
  }, [allKeys, mode]);

  const selectWASD = useCallback(() => {
    const wasdKeys = KEY_GROUPS.WASD.filter((k) => allKeys.includes(k));
    setSelection(wasdKeys);
  }, [allKeys, setSelection]);

  const selectArrows = useCallback(() => {
    const arrowKeys = KEY_GROUPS.ARROWS.filter((k) => allKeys.includes(k));
    setSelection(arrowKeys);
  }, [allKeys, setSelection]);

  const selectFunctionKeys = useCallback(() => {
    const fnKeys = KEY_GROUPS.FUNCTION_KEYS.filter((k) => allKeys.includes(k));
    setSelection(fnKeys);
  }, [allKeys, setSelection]);

  const selectNumberRow = useCallback(() => {
    const numKeys = KEY_GROUPS.NUMBER_ROW.filter((k) => allKeys.includes(k));
    setSelection(numKeys);
  }, [allKeys, setSelection]);

  const selectCustom = useCallback(
    (codes: string[]) => {
      const validKeys = codes.filter((k) => allKeys.includes(k));
      setSelection(validKeys);
    },
    [allKeys, setSelection]
  );

  // ========== 辅助信息 ==========

  const isSelected = useCallback(
    (key: string) => selectedKeys.includes(key),
    [selectedKeys]
  );

  const selectionCount = selectedKeys.length;
  const totalKeys = allKeys.length;

  return {
    selectedKeys,
    selectedPositions,
    selectKey,
    deselectKey,
    toggleKey,
    clearSelection,
    setSelection,
    selectAll,
    selectInvert,
    selectWASD,
    selectArrows,
    selectFunctionKeys,
    selectNumberRow,
    selectCustom,
    isSelected,
    selectionCount,
    totalKeys,
    allKeys,
  };
};
